<template>
    <div class="form-group col-12 d-flex flex-column">
        <label for="source-of-referral">
            Referral Source
            <span class="text-danger">*</span>
        </label>
        <div class="d-flex flex-wrap" v-if="referralSources.length > 0">
            <div v-for="source of referralSources" class="form-check custom-radio form-check-inline d-inline">
                <input
                    type="radio"
                    :id="`source-of-referral-${source.name}`"
                    class="form-check-input"
                    name="source-of-referral"
                    :checked="referral.sourceOfReferral === source.value"
                    :value="source.value"
                    v-model="referral.sourceOfReferral"
                    @change="sourceOfReferralUpdated()" 
                />
                <label class="form-check-label" :for="`source-of-referral-${source.name}`">
                    <span class="me-1">{{ source.name }}</span>
                </label>
            </div>
        </div>
    </div>
    <section class="col-12 card flex-row py-2 px-3 row g-0 mb-2">
        <template v-if="referral.sourceOfReferral == 1">
            <div class="form-group col-12 col-lg-4 pe-lg-3">
                <label for="referral-source">
                    <span>Facility Name <span class="text-danger">*</span></span>
                </label>
                <TypeaheadInput
                    :getData="getVendorList"
                    :value="facility"
                    v-on:valueChanged="updateFacility"
                    placeholder="Start Typing..."
                    v-on:blurredInput="v$.referral.facilityReferralSourceId.$touch()"
                />
                <span v-if="v$.referral.facilityReferralSourceId.$error" class="field-invalid"
                    >Facility Name is required.</span
                >
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-3">
                <label for="referral-name">
                    <span>Facility Point of Contact Name</span>
                </label>
                <input
                    class="form-control"
                    maxlength="50"
                    type="text"
                    v-model="referral.facilityReferralSourceContactName"
                />
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-3" v-if="isApplicationPalliative">
                <label for="referral-source">
                    <span>Referring Provider</span>
                </label>
                <TypeaheadInput
                    :getData="getPhysicianList"
                    :value="physician"
                    v-on:valueChanged="updateReferringProvider"
                    :init-value="physician"
                    placeholder="Search Referring Provider "
                />
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="community-liaison"> Community Liaison </label>
                <TypeaheadInput
                    :getData="getUsersByRole"
                    :value="communityLiaison"
                    v-on:valueChanged="updateCommunityLiaison"
                    :init-value="communityLiaison"
                    placeholder="Search Community Liaison"
                />
            </div>
        </template>
        <template v-if="referral.sourceOfReferral == 2">
            <div class="form-group col-12 col-lg-4 pe-lg-3">
                <label for="referral-source">
                    Referring {{ isApplicationPalliative ? "Provider" : "Physician" }} Name
                    <span class="text-danger">*</span>
                </label>
                <TypeaheadInput
                    :getData="getPhysicianList"
                    :value="physician"
                    v-on:valueChanged="updatePhysician"
                    v-on:blurredInput="v$.referral.physicianReferralSourceName.$touch()"
                    :init-value="physician"
                    placeholder="Start Typing..."
                />
                <span v-if="v$.referral.physicianReferralSourceName.$error" class="field-invalid"
                    >Provider Name is required.</span
                >
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-3">
                <label for="referral-name">
                    <span>{{ isApplicationPalliative ? "Provider" : "Physician" }} Point of Contact Name</span>
                </label>
                <input
                    class="form-control"
                    maxlength="50"
                    type="text"
                    placeholder="Enter Name"
                    v-model="referral.physicianReferralSourceContactName"
                />
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-3" v-if="isApplicationPalliative">
                <label for="referral-source"> Referring Facility </label>
                <TypeaheadInput
                    :getData="getVendorList"
                    :value="referringFacility"
                    v-on:valueChanged="updateReferringFacility"
                    placeholder="Search Referring Facility"
                />
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="community-liaison"> Community Liaison </label>
                <TypeaheadInput
                    :getData="getUsersByRole"
                    :value="communityLiaison"
                    v-on:valueChanged="updateCommunityLiaison"
                    :init-value="communityLiaison"
                    placeholder="Search Community Liaison"
                />
            </div>
            <div class="w-100"></div>
            <div class="form-group col-12 col-lg-4" v-if="!isApplicationPalliative">
                <div class="form-check custom-checkbox form-check-inline">
                    <input
                        type="checkbox"
                        id="referral-same-attending-physician"
                        class="form-check-input au-target"
                        v-model="referral.sameAsAttendingPhysician"
                    />
                    <label class="form-check-label" for="referral-same-attending-physician">
                        <span class="me-1">Will serve as Attending Physician</span>
                    </label>
                </div>
            </div>
        </template>
        <template v-if="referral.sourceOfReferral == 3">
            <div class="form-group col-12 col-lg-4 pe-lg-2">
                <label for="internal-referral-source">
                    Other Referral Source Name <span v-if="isApplicationPalliative" class="text-danger">*</span>
                </label>
                <input
                    class="form-control"
                    maxlength="50"
                    type="text"
                    placeholder="Enter Name of Referral Source"
                    v-model="referral.otherReferralSourceName"
                    @blur="v$.referral.otherReferralSourceName.$touch()"
                />
                <span v-if="v$.referral.otherReferralSourceName.$error" class="field-invalid"
                    >Other Referral Source Name is required.</span
                >
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-2">
                <label for="referral-source">
                    <span>Relationship</span>
                </label>
                <select name="relationship" class="form-select" v-model="referral.otherReferralSourceRelationship">
                    <option :value="null">Select Relationship</option>
                    <option v-for="rel of relationships" :value="rel.value">{{ rel.name }}</option>
                </select>
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-2" v-if="showRelationshipDescription">
                <label for="referral-name">
                    <span>Describe Other Relationship</span>
                </label>
                <input
                    class="form-control"
                    type="text"
                    placeholder="Enter Relationship"
                    v-model="referral.otherReferralSourceRelationshipDescription"
                />
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="community-liaison"> Community Liaison </label>
                <TypeaheadInput
                    :getData="getUsersByRole"
                    :value="communityLiaison"
                    v-on:valueChanged="updateCommunityLiaison"
                    :init-value="communityLiaison"
                    placeholder="Search Community Liaison"
                />
            </div>
        </template>
        <template v-if="referral.sourceOfReferral == 4">
            <div class="form-group col-12 col-lg-4 pe-lg-2">
                <label for="internal-referral-source-name">
                    Internal Referral Source Name
                    <span v-if="isApplicationPalliative" class="text-danger">*</span></label
                >
                <TypeaheadInput
                    :getData="getUsersByRole"
                    :value="internalReferralSource"
                    v-on:valueChanged="updateInternalReferralSource"
                    :init-value="internalReferralSource"
                    v-on:blurredInput="v$.referral.internalReferralSourceId.$touch()"
                    placeholder="Start Typing..."
                />
                <span v-if="v$.referral.internalReferralSourceId.$error" class="field-invalid"
                    >Internal Referral Source Name is required.</span
                >
            </div>
            <div class="form-group col-12 col-lg-4 pe-lg-2">
                <label for="internal-referral-source"> Internal Referral Source </label>
                <select name="internalReferralSource" class="form-select" v-model="referral.internalReferralSource">
                    <option :value="0">Select Source</option>
                    <option v-for="data of InternalReferralSource" :value="data.value">
                        {{ data.name }}
                    </option>
                </select>
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="community-liaison"> Community Liaison </label>
                <TypeaheadInput
                    :getData="getUsersByRole"
                    :value="communityLiaison"
                    v-on:valueChanged="updateCommunityLiaison"
                    :init-value="communityLiaison"
                    placeholder="Search Community Liaison"
                />
            </div>
        </template>
        <div
            class="form-group col-12 col-lg-4"
            v-if="isApplicationPalliative && (referral.sourceOfReferral == 2 || referral.sourceOfReferral == 1)"
        >
            <button
                type="button"
                :class="`btn btn-primary ${referral.sourceOfReferral == 1 ? 'mt-4' : ''}`"
                v-tooltip="{
                    title: `You will be redirected to solution to add 
                ${referral.sourceOfReferral == 2 ? 'provider' : 'facility'} without navigating from current screen.`
                }"
                @click="handleRedirection(referral.sourceOfReferral)"
            >
                Add {{ referral.sourceOfReferral == 2 ? "Provider" : "Facility" }}
            </button>
        </div>
    </section>
    <div class="w-100"></div>
    <div class="form-group col-12">
        <label for="referral-comments"> Comments </label>
        <CommentInput
            input-name="referral-comments"
            :result="referral.comments"
            v-on:resultUpdatedAppended="resultUpdatedAppended"
            v-on:resultUpdated="resultUpdated"
            placeholder="Enter Comments"
            :class-list="commentInputClass"
            :application="referral.application"
        >
        </CommentInput>
    </div>
</template>

<script lang="ts" src="./NewReferralInformation.ts"></script>
<style src="./NewReferralInformation.scss"></style>
